import { useEffect, useMemo, useState } from "react";

import { Button, Checkbox, OpenIcon } from "../../components/ui";
import {
  useAgentDocuments,
  type AgentDocument,
  type AgentStoredDocument,
  type XLMModel,
} from "../api/e2e";
import { documentSourceDomain } from "../lib/documents";
import {
  modelDocumentCellMentions,
  modelReferenceTarget,
  type ModelDocumentCellMention,
  type ModelReference,
} from "../lib/modelReferences";
import AgentDocumentCatalog, {
  DOCUMENT_SORTS,
  type DocumentSort,
  type DocumentTypeFilter,
} from "./AgentDocumentCatalog";
import AgentDocumentHierarchyBadge from "./AgentDocumentHierarchyBadge";

export function documentsForRun(
  documents: AgentDocument[],
  runId: string,
): AgentStoredDocument[] {
  return documents.filter((document) => document.run_ids.includes(runId));
}

export function citedDocuments(
  documents: AgentStoredDocument[],
  citedDocumentIds: ReadonlySet<string>,
): AgentStoredDocument[] {
  return documents.filter((document) => citedDocumentIds.has(document.id));
}

function mentionsByDocument(
  model: XLMModel,
): ReadonlyMap<string, ModelDocumentCellMention[]> {
  const byDocument = new Map<string, ModelDocumentCellMention[]>();
  for (const mention of modelDocumentCellMentions(model)) {
    const existing = byDocument.get(mention.documentId);
    if (existing) existing.push(mention);
    else byDocument.set(mention.documentId, [mention]);
  }
  return byDocument;
}

function mentionsForDocument(
  mentions: ReadonlyMap<string, ModelDocumentCellMention[]> | null,
  documentId: string,
): readonly ModelDocumentCellMention[] | null {
  return mentions === null ? null : (mentions.get(documentId) ?? []);
}

function cellCountLabel(
  mentions: readonly ModelDocumentCellMention[] | null,
  loading: boolean,
): string {
  if (mentions === null) return loading ? "Loading…" : "Unavailable";
  if (mentions.length === 0) return "Not cited";
  return `${mentions.length.toLocaleString()} cited ${
    mentions.length === 1 ? "cell" : "cells"
  }`;
}

function documentOrigin(document: AgentStoredDocument): string {
  if (document.source_type === "tucker_document") return "Tucker ontology";
  if (document.source_type === "agent_fetched") return "Agent fetched";
  return "Analyst upload";
}

export function ModelDocumentCellSummary({
  document,
  mentions,
  loading,
  onOpen,
}: {
  document: AgentStoredDocument;
  mentions: readonly ModelDocumentCellMention[] | null;
  loading: boolean;
  onOpen: () => void;
}) {
  const label = cellCountLabel(mentions, loading);
  return (
    <Button
      tone="quiet"
      size="sm"
      className="model-document-cells__trigger"
      aria-label={`Open side peek for ${document.name}, file ${document.filename}, ${label}`}
      onClick={onOpen}
    >
      {label}
    </Button>
  );
}

export function ModelDocumentSidePeek({
  document,
  mentions,
  loading,
  onClose,
  onOpenReference,
}: {
  document: AgentStoredDocument;
  mentions: readonly ModelDocumentCellMention[] | null;
  loading: boolean;
  onClose: () => void;
  onOpenReference: (reference: ModelReference) => void;
}) {
  const countLabel = cellCountLabel(mentions, loading);
  return (
    <aside
      className="agent-cell-sidepeek model-document-sidepeek"
      aria-label={`Document side peek for ${document.name}`}
    >
      <header>
        <div>
          <span className="eyebrow">{countLabel}</span>
          <h2 title={document.name}>{document.name}</h2>
          <small title={document.filename}>{document.filename}</small>
        </div>
        <div className="agent-cell-sidepeek__actions">
          <button
            type="button"
            aria-label="Close document side peek"
            title="Close document side peek"
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </header>

      <dl className="definition-list model-document-sidepeek__metadata">
        <div>
          <dt>Description</dt>
          <dd>{document.description.trim() || "No description provided."}</dd>
        </div>
        <div>
          <dt>Origin</dt>
          <dd>{documentOrigin(document)}</dd>
        </div>
        <div>
          <dt>Released</dt>
          <dd>{document.release_date ?? "—"}</dd>
        </div>
        <div>
          <dt>Evidence hierarchy</dt>
          <dd>
            <AgentDocumentHierarchyBadge hierarchy={document.hierarchy} />
          </dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>
            {document.source_url ? (
              <a href={document.source_url} target="_blank" rel="noreferrer">
                {documentSourceDomain(document.source_url)} <OpenIcon />
              </a>
            ) : (
              "No public source"
            )}
          </dd>
        </div>
      </dl>

      <section className="model-document-sidepeek__cells">
        <h3>Referenced model cells</h3>
        {mentions === null ? (
          <p>{loading ? "Loading model cells…" : "Model cells unavailable."}</p>
        ) : mentions.length === 0 ? (
          <p>No populated model cells cite this document.</p>
        ) : (
          <ul aria-label="Referenced model cells">
            {mentions.map((mention) => (
              <li key={modelReferenceTarget(mention.reference)}>
                <Button
                  tone="quiet"
                  className="model-document-sidepeek__link"
                  aria-label={`Open ${mention.lineItemLabel}, ${mention.periodLabel} in the model`}
                  onClick={() => onOpenReference(mention.reference)}
                >
                  <span className="model-document-sidepeek__cell-content">
                    <span className="model-document-sidepeek__cell-target">
                      <strong>{mention.lineItemLabel}</strong>
                      <small>{mention.periodLabel}</small>
                      <span className="model-document-sidepeek__cell-value">
                        <span>Value</span>
                        <strong>{mention.valueLabel}</strong>
                      </span>
                    </span>
                    <span className="model-document-sidepeek__annotation">
                      <span>Comment</span>
                      <span>{mention.comment ?? "No cell comment."}</span>
                    </span>
                    <span className="model-document-sidepeek__annotation">
                      <span>Evidence</span>
                      <span>
                        {mention.evidence ?? "No reference evidence."}
                      </span>
                    </span>
                    <span className="model-document-sidepeek__annotation">
                      <span>
                        {mention.excerpts.length === 1 ? "Excerpt" : "Excerpts"}
                      </span>
                      {mention.excerpts.length > 0 ? (
                        mention.excerpts.map((excerpt) => (
                          <span
                            key={excerpt}
                            className="model-document-sidepeek__excerpt"
                          >
                            “{excerpt}”
                          </span>
                        ))
                      ) : (
                        <span>No location excerpt.</span>
                      )}
                    </span>
                  </span>
                  <OpenIcon />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}

export default function AgentModelDocumentsPanel({
  companyId,
  runId,
  model,
  modelLoading = false,
  onOpenReference,
}: {
  companyId: string;
  runId: string;
  model?: XLMModel;
  modelLoading?: boolean;
  onOpenReference: (reference: ModelReference) => void;
}) {
  const [search, setSearch] = useState("");
  const [documentType, setDocumentType] = useState<DocumentTypeFilter>("all");
  const [sort, setSort] = useState<DocumentSort>("release_desc");
  const [citedOnly, setCitedOnly] = useState(true);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const documents = useAgentDocuments(companyId, {
    search,
    documentType: documentType === "all" ? undefined : documentType,
    runId,
    ...DOCUMENT_SORTS[sort],
  });
  const modelDocuments = documentsForRun(documents.data ?? [], runId);
  const cellMentions = useMemo(
    () => (model ? mentionsByDocument(model) : null),
    [model],
  );
  const displayedDocuments = citedOnly
    ? cellMentions === null
      ? []
      : citedDocuments(modelDocuments, new Set(cellMentions.keys()))
    : modelDocuments;
  const selectedDocument =
    displayedDocuments.find((document) => document.id === selectedDocumentId) ??
    null;

  useEffect(() => {
    if (selectedDocumentId !== null && selectedDocument === null) {
      setSelectedDocumentId(null);
    }
  }, [selectedDocument, selectedDocumentId]);

  useEffect(() => {
    setCitedOnly(true);
    setSelectedDocumentId(null);
  }, [runId]);

  if (documents.isPending && !documents.data) {
    return (
      <div className="model-documents-panel__state">Loading documents…</div>
    );
  }
  if (documents.error && !documents.data) {
    return (
      <div className="model-documents-panel__state" role="alert">
        Documents could not be loaded: {documents.error.message}
      </div>
    );
  }

  return (
    <div
      className={`model-documents-workspace${selectedDocument ? " has-sidepeek" : ""}`}
    >
      <div className="model-documents-workspace__catalog">
        <AgentDocumentCatalog
          companyId={companyId}
          documents={displayedDocuments}
          isFetching={documents.isFetching}
          search={search}
          onSearchChange={setSearch}
          documentType={documentType}
          onDocumentTypeChange={setDocumentType}
          sort={sort}
          onSortChange={setSort}
          includeOntology={false}
          showCaptureCount={false}
          actions={
            <Checkbox
              label="Cited documents only"
              aria-label="Cited documents only"
              checked={citedOnly}
              disabled={modelLoading}
              onCheckedChange={setCitedOnly}
            />
          }
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={(document) => setSelectedDocumentId(document.id)}
          contextColumn={{
            header: "Model cells",
            className: "model-document-cells",
            render: (document) => (
              <ModelDocumentCellSummary
                document={document}
                mentions={mentionsForDocument(cellMentions, document.id)}
                loading={modelLoading}
                onOpen={() => setSelectedDocumentId(document.id)}
              />
            ),
          }}
          emptyMessage={
            citedOnly && cellMentions === null
              ? modelLoading
                ? "Loading cited documents…"
                : "Citations are unavailable for this model."
              : citedOnly
                ? "No cited documents are available for this model."
                : "No captured documents are linked to this model’s run."
          }
        />
      </div>
      {selectedDocument && (
        <ModelDocumentSidePeek
          document={selectedDocument}
          mentions={mentionsForDocument(cellMentions, selectedDocument.id)}
          loading={modelLoading}
          onClose={() => setSelectedDocumentId(null)}
          onOpenReference={onOpenReference}
        />
      )}
    </div>
  );
}
